import { useState, useEffect } from 'react';
import axiosInstance from '../../../utils/axios';
import { SERVER } from '../../../hooks/config';
import { 
  MdShoppingCart, 
  MdPendingActions, 
  MdLocalShipping, 
  MdCheckCircle,
  MdCancel,
  MdRefresh,
  MdVisibility
} from 'react-icons/md';
import './Orders.scss';
import { MdClose } from "react-icons/md";

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchOrders();
  }, [filter]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/orders');
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        window.location.href = '/admin/login';
      }
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axiosInstance.put(`/orders/${orderId}/status`, { status: newStatus });
      fetchOrders(); // Refresh orders list
      alert('Order status updated successfully');
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Error updating order status');
    }
  };

  const viewOrderDetails = async (orderId) => {
    try {
      const response = await axiosInstance.get(`/orders/${orderId}`);
      setSelectedOrder(response.data);
      setShowOrderModal(true);
    } catch (error) {
      console.error('Error fetching order details:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'TND'
    }).format(amount);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <MdPendingActions className="status-icon pending" />;
      case 'confirmed':
        return <MdCheckCircle className="status-icon confirmed" />;
      case 'processing':
        return <MdRefresh className="status-icon processing" />;
      case 'shipped':
        return <MdLocalShipping className="status-icon shipped" />;
      case 'delivered':
        return <MdCheckCircle className="status-icon delivered" />;
      case 'cancelled':
        return <MdCancel className="status-icon cancelled" />;
      default:
        return <MdShoppingCart className="status-icon" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return '#d69e2e';
      case 'confirmed':
        return '#3182ce';
      case 'processing':
        return '#805ad5';
      case 'shipped':
        return '#319795';
      case 'delivered':
        return '#38a169';
      case 'cancelled':
        return '#e53e3e';
      default:
        return '#718096';
    }
  };

  const filteredOrders = filter === 'all' 
    ? orders 
    : orders.filter(order => order.status === filter);

  if (loading) {
    return (
      <div className="orders-loading">
        <div className="loading-spinner"></div>
        <p>Loading orders...</p>
      </div>
    );
  }

  return (
    <div className="admin-orders">
      <div className="orders-header">
        <div className="header-left">
          <h2>Orders Management</h2>
          <p>Manage customer orders</p>
        </div>
        <button className="refresh-btn" onClick={fetchOrders}>
          <MdRefresh />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="orders-filters">
        <button 
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All Orders
        </button>
        <button 
          className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
          onClick={() => setFilter('pending')}
        >
          <MdPendingActions />
          Pending
        </button>
        <button 
          className={`filter-btn ${filter === 'confirmed' ? 'active' : ''}`}
          onClick={() => setFilter('confirmed')}
        >
          <MdCheckCircle />
          Confirmed
        </button>
        <button 
          className={`filter-btn ${filter === 'processing' ? 'active' : ''}`}
          onClick={() => setFilter('processing')}
        >
          <MdRefresh />
          Processing
        </button>
        <button 
          className={`filter-btn ${filter === 'shipped' ? 'active' : ''}`}
          onClick={() => setFilter('shipped')}
        >
          <MdLocalShipping />
          Shipped
        </button>
        <button 
          className={`filter-btn ${filter === 'delivered' ? 'active' : ''}`}
          onClick={() => setFilter('delivered')}
        >
          <MdCheckCircle />
          Delivered
        </button>
        <button 
          className={`filter-btn ${filter === 'cancelled' ? 'active' : ''}`}
          onClick={() => setFilter('cancelled')}
        >
          <MdCancel />
          Cancelled
        </button>
      </div>

      {/* Orders Table */}
      <div className="orders-table-container">
        <table className="orders-table">
          <thead>
            <tr>
              <th>Order Number</th>
              <th>Customer</th>
              <th>Date</th>
              <th>Items</th>
              <th>Total</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.map((order) => (
              <tr key={order.id}>
                <td>
                  <span className="order-number">{order.order_number}</span>
                  {order.is_personalized && (
                    <span className="personalized-badge">Personalized</span>
                  )}
                </td>
                <td>
                  <div className="customer-info">
                    <div className="customer-name">{order.customer_name}</div>
                    <div className="customer-email">{order.customer_email}</div>
                  </div>
                </td>
                <td>
                  <span className="order-date">{formatDate(order.created_at)}</span>
                </td>
                <td>
                  <span className="items-count">
                    {order.items_count} item{order.items_count !== 1 ? 's' : ''}
                  </span>
                </td>
                <td>
                  <span className="order-total">{formatCurrency(order.total)}</span>
                </td>
                <td>
                  <div className="status-cell">
                    {getStatusIcon(order.status)}
                    <span 
                      className="status-text"
                      style={{ color: getStatusColor(order.status) }}
                    >
                      {order.status}
                    </span>
                  </div>
                </td>
                <td>
                  <div className="order-actions">
                    <button
                      className="action-btn view-btn"
                      onClick={() => viewOrderDetails(order.id)}
                      title="View Details"
                    >
                      <MdVisibility />
                    </button>
                    <select
                      className="status-select"
                      value={order.status}
                      onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="processing">Processing</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredOrders.length === 0 && (
          <div className="no-orders">
            <MdShoppingCart />
            <p>No orders found</p>
            {filter !== 'all' && (
              <p className="filter-hint">Try changing your filter settings</p>
            )}
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      {showOrderModal && selectedOrder && (
        <div className="modal-overlay">
          <div className="modal order-details-modal">
            <div className="modal-header">
              <h3>Order Details - {selectedOrder.order.order_number}</h3>
              <button onClick={() => setShowOrderModal(false)}>
                <MdClose />
              </button>
            </div>
            
            <div className="modal-content">
              <div className="order-sections">
                {/* Customer Information */}
                <div className="order-section">
                  <h4>Customer Information</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <span className="info-label">Name:</span>
                      <span className="info-value">{selectedOrder.order.customer_name}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Email:</span>
                      <span className="info-value">{selectedOrder.order.customer_email}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Phone:</span>
                      <span className="info-value">{selectedOrder.order.customer_phone}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Address:</span>
                      <span className="info-value">
                        {selectedOrder.order.customer_address}, {selectedOrder.order.customer_city}, {selectedOrder.order.customer_zip_code}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Order Information */}
                <div className="order-section">
                  <h4>Order Information</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <span className="info-label">Order Date:</span>
                      <span className="info-value">{formatDate(selectedOrder.order.created_at)}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Status:</span>
                      <span 
                        className="info-value status"
                        style={{ color: getStatusColor(selectedOrder.order.status) }}
                      >
                        {getStatusIcon(selectedOrder.order.status)}
                        {selectedOrder.order.status}
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Payment Method:</span>
                      <span className="info-value">{selectedOrder.order.payment_method}</span>
                    </div>
                    {selectedOrder.order.is_personalized && (
                      <div className="info-item">
                        <span className="info-label">Order Type:</span>
                        <span className="info-value personalized">Personalized Order</span>
                      </div>
                    )}
                    {selectedOrder.order.reference_photo && (
                      <div className="info-item">
                        <span className="info-label">Reference Photo:</span>
                        <span className="info-value">
                          <img 
                            src={`${SERVER}${selectedOrder.order.reference_photo}`} 
                            alt="Reference" 
                            className="reference-photo"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'inline';
                            }}
                          />
                          <span style={{display: 'none'}}>Photo not available</span>
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Order Items */}
                <div className="order-section">
                  <h4>Order Items ({selectedOrder.items.length})</h4>
                  <div className="order-items-list">
                    {selectedOrder.items.map((item) => (
                      <div key={item.id} className="order-item-detail">
                        <div className="item-image">
                          {item.product_image ? (
                            <img 
                              src={`${SERVER}${item.product_image}`} 
                              alt={item.product_name}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : (
                            <div className="no-image">No Image</div>
                          )}
                        </div>
                        <div className="item-details">
                          <h5>{item.product_name}</h5>
                          <p>Size: {(item.selected_size)}</p>
                          <p>Quantity: {item.quantity}</p>
                          <p>Price: {formatCurrency(item.product_price)} each</p>
                          <p>Total: {formatCurrency(item.total_price)}</p>
                          {item.customizations && (
                            <div className="customizations">
                              <h6>Customizations:</h6>
                              <div className="customization-details">
                                {Object.entries(item.customizations).map(([key, value]) => (
                                  <div key={key} className="customization-item">
                                    <span className="customization-label">
                                      {key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')}:
                                    </span>
                                    <span className="customization-value">{value}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Order Summary */}
                <div className="order-section">
                  <h4>Order Summary</h4>
                  <div className="order-summary">
                    <div className="summary-item">
                      <span>Subtotal:</span>
                      <span>{formatCurrency(selectedOrder.order.subtotal)}</span>
                    </div>
                    <div className="summary-item">
                      <span>Shipping:</span>
                      <span>{formatCurrency(selectedOrder.order.shipping_cost)}</span>
                    </div>
                    <div className="summary-item total">
                      <span>Total:</span>
                      <span>{formatCurrency(selectedOrder.order.total)}</span>
                    </div>
                  </div>
                </div>

                {selectedOrder.order.order_notes && (
                  <div className="order-section">
                    <h4>Order Notes</h4>
                    <div className="order-notes">
                      <p>{selectedOrder.order.order_notes}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-actions">
              <button 
                type="button" 
                onClick={() => setShowOrderModal(false)}
              >
                Close
              </button>
              <select
                className="status-select"
                value={selectedOrder.order.status}
                onChange={(e) => {
                  updateOrderStatus(selectedOrder.order.id, e.target.value);
                  setShowOrderModal(false);
                }}
              >
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="processing">Processing</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;